import type { CapabilityPropertySchema, ConfidenceLevel, ValueSource } from "@/api/types";
import type { BadgeTone } from "@/components/StatusBadge";

/**
 * Comment une capacité se lit à l'écran (TICKET V1.5-06).
 *
 * Séparé du composant pour être testable : ce sont des règles d'affichage, pas
 * du rendu, et elles ont des cas particuliers — un suffixe d'unité à traduire,
 * une liste à mettre en français, un flottant JSONB dont le zéro décimal ne
 * doit pas donner l'impression d'une précision qui n'existe pas.
 *
 * **Aucun JSON brut ne doit atteindre l'utilisateur.** C'est la règle que ce
 * module fait tenir : `{"max_diameter_mm": 38}` s'écrit « Diamètre maximal :
 * 38 mm ».
 */

export const SOURCE_LABELS: Record<ValueSource, string> = {
  HUMAN: "saisi",
  BARCODE: "code-barres",
  OCR: "texte lu",
  LOCAL_VISION: "modèle local",
  CLOUD_VISION: "modèle cloud",
  MANUFACTURER: "fiche fabricant",
  CATALOGUE: "catalogue",
  SUPPLIER: "fournisseur",
  WEB: "web",
  MEASURED: "mesuré",
  IMPORT: "import",
  INFERRED: "déduit",
  SYSTEM: "système",
};

/** Les sources qu'un humain invoque à la saisie. Le reste vient des pipelines. */
export const ASSERTABLE_SOURCES: ValueSource[] = [
  "HUMAN",
  "MANUFACTURER",
  "CATALOGUE",
  "SUPPLIER",
  "MEASURED",
  "INFERRED",
];

export const CERTAINTY_LABELS: Record<ConfidenceLevel, { label: string; tone: BadgeTone }> = {
  CERTAIN: { label: "Confirmé", tone: "available" },
  LIKELY: { label: "Probable", tone: "reserved" },
  UNCERTAIN: { label: "À vérifier", tone: "warning" },
  DEGRADED: { label: "Partiel", tone: "warning" },
  FAILED: { label: "Échec", tone: "out" },
};

/** Le suffixe d'unité imposé par ADR 0012 §4, et ce qu'on affiche pour lui. */
export const UNIT_SUFFIXES: Record<string, string> = {
  _mm: "mm",
  _mg: "mg",
  _ml: "ml",
  _v: "V",
  _a: "A",
  _ma: "mA",
  _nm: "N·m",
  _c: "°C",
  _hz: "Hz",
  _w: "W",
};

export function unitOf(name: string): string {
  // Le plus long d'abord : « _ma » et « _a » se terminent tous deux par « a »,
  // et un milliampère affiché en ampères est une erreur d'un facteur mille.
  const found = Object.keys(UNIT_SUFFIXES)
    .sort((left, right) => right.length - left.length)
    .find((suffix) => name.endsWith(suffix));
  return found ? (UNIT_SUFFIXES[found] ?? "") : "";
}

/** Le nom d'un paramètre tel qu'un humain le lit, jamais tel que la base le nomme. */
export function parameterLabel(name: string, schema?: CapabilityPropertySchema): string {
  if (schema?.title) return schema.title;
  const stripped = Object.keys(UNIT_SUFFIXES)
    .sort((left, right) => right.length - left.length)
    .reduce(
      (value, suffix) => (value.endsWith(suffix) ? value.slice(0, -suffix.length) : value),
      name,
    );
  const words = stripped.replace(/_/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Une valeur de paramètre, en français et avec son unité.
 *
 * « 38 mm » et non « 38.0 mm » : le JSONB rend un flottant même quand la valeur
 * est entière, et un zéro décimal parasite donne l'impression d'une précision
 * qui n'existe pas.
 */
export function parameterValue(name: string, value: unknown): string {
  if (Array.isArray(value)) return value.map((item) => String(item)).join(", ");
  if (typeof value === "boolean") return value ? "oui" : "non";
  const shown =
    typeof value === "number" && Number.isInteger(value) ? String(value) : String(value);
  const unit = unitOf(name);
  return unit ? `${shown} ${unit}` : shown;
}
